import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Devise, EtatOffre, Langue } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee, precedentDansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';

export type { EtatOffre } from '@artosera/shared';
/** Miroir JSON de offres/commun.ts ; les dates sérialisées sont des chaînes. */
export interface OffrePresentee {
  id: string;
  numero: string;
  contactId: string;
  contactNom: string;
  expositionId: string | null;
  lignes: {
    oeuvreId: string;
    exemplaireId: string | null;
    designation: string;
    cartel: {
      artiste: string;
      titre: string;
      annee?: string;
      technique?: string;
      dimensions?: string;
      numeroInventaire?: string;
      edition?: string;
    };
    prixPublicCentimes: number | null;
    prixProposeCentimes: number;
  }[];
  totalCentimes: number;
  devise: Devise;
  langue: Langue;
  validiteJusquau: string;
  message: string;
  etat: EtatOffre;
  canal: 'courriel' | 'stand' | 'remise' | null;
  echue: boolean;
  envoyeeLe: string | null;
  relancePrevueLe: string | null;
  relanceeLe: string | null;
  accepteeLe: string | null;
  refuseeLe: string | null;
  retireeLe: string | null;
  expireeLe: string | null;
  motif: string;
  envois: { type: 'envoi' | 'relance'; le: string; destinataire: string; resultat: string }[];
  reservationIds: string[];
  factureId: string | null;
  creeeLe: string;
  modifieeLe: string;
}
export interface CorpsOffre {
  contactId: string;
  lignes: { oeuvreId: string; exemplaireId?: string; prixProposeCentimes: number }[];
  validiteJusquau: string;
  message?: string;
  langue?: Langue;
  expositionId?: string;
  canal?: 'stand';
}
export interface HistoriqueContact {
  achats: {
    factures:
      | {
          id: string;
          numero: string | null;
          emiseLe: string | null;
          totalTtcCentimes: number | null;
          devise: string;
          oeuvres: { id: string; titre: string }[];
        }[]
      | 'non_incluses';
    ventesSimples: {
      oeuvreId: string;
      titre: string;
      prixCentimes: number;
      devise: string;
      venduLe: string;
    }[];
  };
  interets: {
    type: 'demande' | 'reservation' | 'offre';
    id: string;
    oeuvres: { id: string; titre: string }[];
    date: string;
    etat: string;
    numero?: string;
  }[];
}
export interface FiltresOffres {
  etat?: EtatOffre;
  aRelancer?: boolean;
  closes?: boolean;
  oeuvreId?: string;
  contactId?: string;
  page?: number;
  parPage?: number;
}
export interface ReponseListeOffres {
  offres: OffrePresentee[];
  compteurs: { brouillon: number; envoyee: number; aRelancer: number };
  pagination: Pagination;
}
export type GesteOffre = 'envoyer' | 'remettre' | 'relancer' | 'accepter' | 'refuser' | 'retirer';
export interface CorpsGesteOffre {
  destinataire?: string;
  message?: string;
  motif?: string;
}
export interface ReponseGesteOffre {
  offre: OffrePresentee;
  envoi?: { resultat: string };
}
const base = (galerie: string) => `/g/${encodeURIComponent(galerie)}/offres`;
const cible = (galerie: string, id: string) => `${base(galerie)}/${encodeURIComponent(id)}`;
function query(filtres: FiltresOffres): string {
  const params = new URLSearchParams();
  for (const [cle, valeur] of Object.entries({ page: 1, parPage: 50, ...filtres }))
    if (valeur !== undefined) params.set(cle, String(valeur));
  return params.toString();
}
const geste = (
  galerie: string,
  id: string,
  nom: GesteOffre,
  corps: CorpsGesteOffre = {},
  appel?: AppelApi,
) => api.post<ReponseGesteOffre>(`${cible(galerie, id)}/${nom}`, corps, { galerie, ...appel });
export const offresApi = {
  lister: (galerie: string, filtres: FiltresOffres = {}, appel?: AppelApi) =>
    api.get<ReponseListeOffres>(`${base(galerie)}?${query(filtres)}`, { galerie, ...appel }),
  lire: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<{ offre: OffrePresentee }>(cible(galerie, id), { galerie, ...appel }),
  creer: (galerie: string, corps: CorpsOffre, appel?: AppelApi) =>
    api.post<{ offre: OffrePresentee }>(base(galerie), corps, { galerie, ...appel }),
  modifier: (galerie: string, id: string, corps: Partial<CorpsOffre>, appel?: AppelApi) =>
    api.patch<{ offre: OffrePresentee }>(cible(galerie, id), corps, { galerie, ...appel }),
  supprimer: (galerie: string, id: string, appel?: AppelApi) =>
    api.delete<void>(cible(galerie, id), { galerie, ...appel }),
  planifierRelance: (galerie: string, id: string, le: string | null, appel?: AppelApi) =>
    api.put<{ offre: OffrePresentee }>(
      `${cible(galerie, id)}/relance`,
      { le },
      { galerie, ...appel },
    ),
  envoyer: (galerie: string, id: string, corps: CorpsGesteOffre = {}, appel?: AppelApi) =>
    geste(galerie, id, 'envoyer', corps, appel),
  remettre: (galerie: string, id: string, appel?: AppelApi) =>
    geste(galerie, id, 'remettre', {}, appel),
  relancer: (galerie: string, id: string, corps: CorpsGesteOffre = {}, appel?: AppelApi) =>
    geste(galerie, id, 'relancer', corps, appel),
  accepter: (galerie: string, id: string, appel?: AppelApi) =>
    geste(galerie, id, 'accepter', {}, appel),
  refuser: (galerie: string, id: string, corps: CorpsGesteOffre = {}, appel?: AppelApi) =>
    geste(galerie, id, 'refuser', corps, appel),
  retirer: (galerie: string, id: string, corps: CorpsGesteOffre = {}, appel?: AppelApi) =>
    geste(galerie, id, 'retirer', corps, appel),
  lier: (
    galerie: string,
    id: string,
    corps: { reservationId?: string; factureId?: string },
    appel?: AppelApi,
  ) =>
    api.post<{ offre: OffrePresentee }>(`${cible(galerie, id)}/liens`, corps, {
      galerie,
      ...appel,
    }),
  pdf: (galerie: string, id: string, appel?: AppelApi) =>
    api.get<ReponseDocumentSigne>(`${cible(galerie, id)}/pdf`, { galerie, ...appel }),
  historique: (galerie: string, contactId: string, appel?: AppelApi) =>
    api.get<HistoriqueContact>(
      `${base(galerie)}/contacts/${encodeURIComponent(contactId)}/historique`,
      { galerie, ...appel },
    ),
};
export function useOffres(
  galerie: string,
  filtres: FiltresOffres = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: cleMetier(galerie, 'offres', 'liste', filtres),
    queryFn: (contexte) => offresApi.lister(galerie, filtres, appelDeRequete(contexte)),
    placeholderData: precedentDansLaPortee(galerie),
    ...options,
  });
}
export function useOffre(galerie: string, id: string | null) {
  return useQuery({
    queryKey: cleMetier(galerie, 'offres', id),
    queryFn: (contexte) => offresApi.lire(galerie, id!, appelDeRequete(contexte)),
    enabled: id !== null,
  });
}
export function useHistoriqueContact(galerie: string, id: string) {
  return useQuery({
    queryKey: cleMetier(galerie, 'offres', 'historique', id),
    queryFn: (contexte) => offresApi.historique(galerie, id, appelDeRequete(contexte)),
  });
}
function useInvalidationOffres(galerie: string) {
  const client = useQueryClient();
  return () => {
    for (const domaine of ['offres', 'reservations', 'ventes', 'oeuvres'])
      void client.invalidateQueries({ queryKey: cleMetier(galerie, domaine) });
  };
}
export function useCreerOffre(galerie: string) {
  const invalider = useInvalidationOffres(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsOffre, appel) => offresApi.creer(galerie, corps, appel),
      onSuccess: invalider,
    }),
  );
}
export function useModifierOffre(galerie: string, id: string) {
  const invalider = useInvalidationOffres(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: Partial<CorpsOffre>, appel) =>
        offresApi.modifier(galerie, id, corps, appel),
      onSuccess: invalider,
    }),
  );
}
export function useSupprimerOffre(galerie: string, id: string) {
  const invalider = useInvalidationOffres(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: (_: void, appel) => offresApi.supprimer(galerie, id, appel),
      onSuccess: invalider,
    }),
  );
}
export function useGesteOffre(galerie: string, id: string) {
  const invalider = useInvalidationOffres(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: ({ geste: nom, corps }: { geste: GesteOffre; corps?: CorpsGesteOffre }, appel) =>
        geste(galerie, id, nom, corps, appel),
      onSuccess: invalider,
    }),
  );
}
export function useLierOffre(galerie: string, id: string) {
  const invalider = useInvalidationOffres(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: { reservationId?: string; factureId?: string }, appel) =>
        offresApi.lier(galerie, id, corps, appel),
      onSuccess: invalider,
    }),
  );
}
export function usePlanifierRelance(galerie: string, id: string) {
  const invalider = useInvalidationOffres(galerie);
  return useMutation(
    dansLaPortee({
      mutationFn: (le: string | null, appel) => offresApi.planifierRelance(galerie, id, le, appel),
      onSuccess: invalider,
    }),
  );
}
